import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { MqttSuspense } from "@/components/generic/mqtt-suspense";
import { MultiPropertyCardList } from "@/components/generic/multi-property-card-list";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import type { LibraryBlank, MagazineSlot } from "@/data-interfaces/blanks";
import type { ApiError } from "@/data-interfaces/generics";
import type { RealTimeData } from "@/data-interfaces/real-time-data";
import { useMQTTAutoRefreshPublish, useMQTTSubscribe } from "@/hooks/use-mqtt";
import {
	getLibraryBlanks,
	getMagazineSlots,
	isLibraryVisibile,
	isMagazineVisibile,
} from "@/lib/data-remappings/blanks";
import { BLANKS_PAGE_URL, getAppWrappedPageUrl } from "@/lib/page-routes";
import {
	BL_LIBRARY_BLANKS_URL,
	BL_MAGAZINE_SLOTS_URL,
	getRtdSubscriptionTopic,
} from "@/lib/request-paths";
import { getMachineSerialNumber } from "@/lib/session-storage-keys";

export const Route = createFileRoute(getAppWrappedPageUrl(BLANKS_PAGE_URL))({
	component: Blanks,
	validateSearch: (search: Record<string, unknown>) => {
		const defaultValue = isMagazineVisibile() ? "magazine" : "library";
		return search.selectedTab ?? { selectedTab: defaultValue };
	},
});

function Blanks() {
	const [t, _] = useTranslation();

	const navigate = useNavigate();

	const { selectedTab } = Route.useSearch();

	const [magazineSlots, setMagazineSlots] = useState<
		Array<MagazineSlot> | ApiError | null
	>(null);
	const [blanks, setBlanks] = useState<Array<LibraryBlank> | ApiError | null>(
		null,
	);
	const [realTimeData, setRealTimeData] = useState<
		RealTimeData | ApiError | null
	>(null);

	useMQTTSubscribe(BL_MAGAZINE_SLOTS_URL, setMagazineSlots);
	useMQTTSubscribe(BL_LIBRARY_BLANKS_URL, setBlanks);
	useMQTTSubscribe(
		getRtdSubscriptionTopic(getMachineSerialNumber()),
		setRealTimeData,
		true,
	);

	useMQTTAutoRefreshPublish(30, BL_MAGAZINE_SLOTS_URL);
	useMQTTAutoRefreshPublish(30, BL_LIBRARY_BLANKS_URL);

	return (
		<div className="text-white p-8">
			<h1 className="text-3xl sm:text-4xl font-medium">{t("Blanks")}</h1>

			<Tabs defaultValue={selectedTab} className="flex flex-1 gap-y-8 mt-8">
				<div className="flex flex-1 flex-row justify-center">
					<TabsList>
						{isMagazineVisibile() && (
							<TabsTrigger value="magazine" className="min-w-25">
								{t("Magazine")}
							</TabsTrigger>
						)}

						{isLibraryVisibile() && (
							<TabsTrigger value="library" className="min-w-25">
								{t("Library")}
							</TabsTrigger>
						)}
					</TabsList>
				</div>

				<TabsContent value="magazine">
					<MqttSuspense
						objects={magazineSlots}
						requestUrl={BL_MAGAZINE_SLOTS_URL}
						genericError="Error retrieving magazine slots"
					>
						{(magazineSlots) => (
							<MultiPropertyCardList
								multiPropertyObjects={getMagazineSlots(
									magazineSlots,
									realTimeData,
									navigate,
								)}
								noItemsLabel="No blank in magazine"
							/>
						)}
					</MqttSuspense>
				</TabsContent>

				<TabsContent value="library">
					<MqttSuspense
						objects={blanks}
						requestUrl={BL_LIBRARY_BLANKS_URL}
						genericError="Error retrieving library blanks"
					>
						{(blanks) => (
							<MultiPropertyCardList
								multiPropertyObjects={getLibraryBlanks(blanks, navigate)}
								noItemsLabel="No blank in library"
								pagination={true}
							/>
						)}
					</MqttSuspense>
				</TabsContent>
			</Tabs>
		</div>
	);
}
